import { useEffect, useRef, useState } from "react";
import { prefersReducedMotion } from "@/lib/reveal-motion";

const MIN_DURATION_MS = 720;
const MAX_DURATION_MS = 2600;

export function authoredMotionDuration(contentLength: number): number {
  const scaled = MIN_DURATION_MS + Math.sqrt(Math.max(0, contentLength)) * 34;
  return Math.min(MAX_DURATION_MS, Math.round(scaled));
}

/**
 * Keeps complete frame content mounted while briefly staging its presentation.
 * Initial streaming skeletons and later content replacements both trigger a
 * bounded pulse; edit mode and reduced-motion preferences bypass it.
 */
export function useAuthoredMotion(
  contentKey: string,
  initialMotion: boolean,
  forceComplete = false,
): { active: boolean; cycle: "a" | "b" } {
  const reducedMotion = prefersReducedMotion();
  const [active, setActive] = useState(
    () => initialMotion && !forceComplete && !reducedMotion,
  );
  const previousKeyRef = useRef(contentKey);
  const revisionRef = useRef(initialMotion ? 1 : 0);
  const changed = previousKeyRef.current !== contentKey;
  if (changed) {
    previousKeyRef.current = contentKey;
    revisionRef.current += 1;
  }

  useEffect(() => {
    if (forceComplete || reducedMotion) {
      setActive(false);
      return;
    }
    if (!active && !changed) return;

    setActive(true);
    const timeout = setTimeout(
      () => setActive(false),
      authoredMotionDuration(contentKey.length),
    );
    return () => clearTimeout(timeout);
  }, [active, changed, contentKey.length, forceComplete, reducedMotion]);

  return {
    active: !forceComplete && !reducedMotion && (active || changed),
    cycle: revisionRef.current % 2 === 0 ? "a" : "b",
  };
}
